import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PropertyService } from '../../../../core/services/property.service';
import { TranslationService } from '../../../../core/services/translation.service';
import { UnitDetailsFieldsComponent } from '../../../../shared/components/unit-details-fields/unit-details-fields';
import { ListingAttributesFieldsComponent } from '../../../../shared/components/listing-attributes-fields/listing-attributes-fields';
import { AMENITIES, FINISHINGS, PAYMENT_OPTIONS } from '../../../../core/models/listing-options';
import { PhaseService } from '../../../../core/services/phase.service';
import { Phase } from '../../../../core/models/phase.model';
import { CompoundService } from '../../../../core/services/compound.service';
import { Compound } from '../../../../core/models/compound.model';
import { Property, CreatePropertyDto, PropertyType, PropertyStatus, ListingType, PROPERTY_TYPE_LABELS, PROPERTY_STATUS_LABELS, LISTING_TYPE_LABELS } from '../../../../core/models/property.model';
import { MediaUploaderComponent } from '../../../../shared/components/media-uploader/media-uploader';

@Component({
  selector: 'app-property-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, ListingAttributesFieldsComponent, UnitDetailsFieldsComponent, MediaUploaderComponent],
  templateUrl: './property-detail.html',
  styleUrl: './property-detail.scss',
})
export class PropertyDetailComponent implements OnInit {
  property:       Property | null = null;
  isLoading       = false;
  isEditMode      = false;
  isSubmitting    = false;
  isDeleting      = false;
  showDeleteModal = false;
  errorMessage    = '';
  successMessage  = '';

  editForm: Partial<CreatePropertyDto> = {};
  imageInput = '';

  translating = {
    titleToEn: false,
    titleToAr: false,
    descToEn:  false,
    descToAr:  false,
  };

  translateErrors = {
    titleToEn: false,
    titleToAr: false,
    descToEn:  false,
    descToAr:  false,
  };

  // Lightbox
  lightboxIndex: number | null = null;

  readonly propertyTypes: { value: PropertyType; en: string; ar: string }[] =
    (Object.keys(PROPERTY_TYPE_LABELS) as PropertyType[]).map(key => ({
      value: key, ...PROPERTY_TYPE_LABELS[key],
    }));

  readonly propertyStatuses: { value: PropertyStatus; en: string; ar: string }[] =
    (Object.keys(PROPERTY_STATUS_LABELS) as PropertyStatus[]).map(key => ({
      value: key, ...PROPERTY_STATUS_LABELS[key],
    }));

  readonly listingTypes: { value: ListingType; en: string; ar: string }[] =
    (Object.keys(LISTING_TYPE_LABELS) as ListingType[]).map(key => ({
      value: key, ...LISTING_TYPE_LABELS[key],
    }));

  listingLabel(type: string, lang: 'en' | 'ar' = 'en'): string {
    return LISTING_TYPE_LABELS[type as ListingType]?.[lang] ?? type;
  }

  typeLabel(type: string, lang: 'en' | 'ar' = 'en'): string {
    return PROPERTY_TYPE_LABELS[type as PropertyType]?.[lang] ?? type;
  }

  statusLabel(status: string, lang: 'en' | 'ar' = 'en'): string {
    return PROPERTY_STATUS_LABELS[status as PropertyStatus]?.[lang] ?? status;
  }

  readonly states = [
    { value: 'cairo',           label: 'القاهرة'         },
    { value: 'north_coast',     label: 'الساحل الشمالي'  },
    { value: 'sharm_el_sheikh', label: 'شرم الشيخ'       },
  ];
  readonly stateLabels: Record<string, string> = Object.fromEntries(
    this.states.map(s => [s.value, s.label])
  );

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private propertyService: PropertyService,
    private translationService: TranslationService,
    private compoundService: CompoundService,
    private phaseService: PhaseService,
    private cdr: ChangeDetectorRef,
  ) {}

  compounds: Compound[] = [];

  phases: Phase[] = [];

  /** Phases of the chosen compound; a unit can sit in one of them. */
  loadPhases(compoundId: number | null | undefined, keep = true): void {
    if (!compoundId) { this.phases = []; return; }
    this.phaseService.getForCompound(compoundId).subscribe({
      next: (res) => {
        this.phases = res.data;
        if (!keep) this.editForm.phase_id = null;
        this.cdr.detectChanges();
      },
      error: () => {},
    });
  }

  onCompoundChange(): void { this.loadPhases(this.editForm.compound_id, false); }

  compoundName(id: number | null | undefined): string {
    const c = this.compounds.find((x) => x.id === id);
    return c ? (c.name_en || c.name_ar) : (id ? `#${id}` : '—');
  }

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.load(id);
    this.compoundService.getAll().subscribe({
      next: (res) => { this.compounds = res.data; this.cdr.detectChanges(); },
      error: () => {},
    });
  }

  load(id: number): void {
    this.isLoading = true;
    this.propertyService.getById(id).subscribe({
      next: (res) => {
        this.property  = res.data;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.isLoading = false;
        this.cdr.detectChanges();
        this.router.navigate(['/dashboard/properties']);
      },
    });
  }

  enableEdit(): void {
    if (!this.property) return;
    this.loadPhases(this.property.compound_id);
    this.editForm = {
      title:           this.property.title,
      title_ar:        this.property.title_ar,
      title_en:        this.property.title_en,
      slug:            this.property.slug,
      content:         this.property.content,
      content_ar:      this.property.content_ar,
      content_en:      this.property.content_en,
      content_html:    this.property.content_html,
      property_type:   this.property.property_type,
      property_status: this.property.property_status,
      listing_type:    this.property.listing_type,
      price:           this.property.price,
      size:            this.property.size,
      bedrooms:        this.property.bedrooms,
      bathrooms:       this.property.bathrooms,
      country:         this.property.country,
      state:           this.property.state,
      compound_id:      this.property.compound_id,
      phase_id:         this.property.phase_id ?? null,
      delivery_date:    this.property.delivery_date ?? null,
      finishing:        this.property.finishing ?? null,
      payment_options:  this.property.payment_options ?? null,
      down_payment_percent: this.property.down_payment_percent == null ? null : Number(this.property.down_payment_percent),
      installment_years:    this.property.installment_years ?? null,
      features:         this.property.features ?? null,
      garden_size:      this.property.garden_size == null ? null : Number(this.property.garden_size),
      level_ar:         this.property.level_ar ?? '',
      level_en:         this.property.level_en ?? '',
      floor_plan:       this.property.floor_plan ?? '',
      maintenance_ar:   this.property.maintenance_ar ?? '',
      maintenance_en:   this.property.maintenance_en ?? '',
      images:          [...(this.property.images || [])],
      video_url:       this.property.video_url,
      is_featured:     this.property.is_featured,
      is_active:       this.property.is_active,
      agent_name:      this.property.agent_name,
      agent_mobile:    this.property.agent_mobile,
      agent_whatsapp:  this.property.agent_whatsapp,
      agent_email:     this.property.agent_email,
      agent_picture:   this.property.agent_picture,
    };
    this.errorMessage = '';
    this.imageInput   = '';
    this.isEditMode   = true;
    this.cdr.detectChanges();
  }

  cancelEdit(): void {
    this.isEditMode   = false;
    this.errorMessage = '';
    this.imageInput   = '';
  }

  addImage(): void {
    const v = this.imageInput.trim();
    if (!v) return;
    this.editForm.images = [...(this.editForm.images || []), v];
    this.imageInput = '';
  }

  removeImage(i: number): void {
    this.editForm.images = (this.editForm.images || []).filter((_, idx) => idx !== i);
  }

  onImageKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter') { e.preventDefault(); this.addImage(); }
  }

  onImagesUploaded(urls: string[]): void {
    this.editForm.images = [...(this.editForm.images || []), ...urls];
  }

  translateTitleToEn(): void {
    if (!this.editForm.title_ar?.trim() || this.translating.titleToEn) return;
    this.translating.titleToEn = true;
    this.translateErrors.titleToEn = false;
    this.translationService.translate(this.editForm.title_ar, 'ar', 'en').subscribe(result => {
      if (result !== null) this.editForm.title_en = result;
      else this.translateErrors.titleToEn = true;
      this.translating.titleToEn = false;
      this.cdr.detectChanges();
    });
  }

  translateTitleToAr(): void {
    if (!this.editForm.title_en?.trim() || this.translating.titleToAr) return;
    this.translating.titleToAr = true;
    this.translateErrors.titleToAr = false;
    this.translationService.translate(this.editForm.title_en, 'en', 'ar').subscribe(result => {
      if (result !== null) this.editForm.title_ar = result;
      else this.translateErrors.titleToAr = true;
      this.translating.titleToAr = false;
      this.cdr.detectChanges();
    });
  }

  translateDescToEn(): void {
    if (!this.editForm.content_ar?.trim() || this.translating.descToEn) return;
    this.translating.descToEn = true;
    this.translateErrors.descToEn = false;
    this.translationService.translate(this.editForm.content_ar, 'ar', 'en').subscribe(result => {
      if (result !== null) this.editForm.content_en = result;
      else this.translateErrors.descToEn = true;
      this.translating.descToEn = false;
      this.cdr.detectChanges();
    });
  }

  translateDescToAr(): void {
    if (!this.editForm.content_en?.trim() || this.translating.descToAr) return;
    this.translating.descToAr = true;
    this.translateErrors.descToAr = false;
    this.translationService.translate(this.editForm.content_en, 'en', 'ar').subscribe(result => {
      if (result !== null) this.editForm.content_ar = result;
      else this.translateErrors.descToAr = true;
      this.translating.descToAr = false;
      this.cdr.detectChanges();
    });
  }

  saveEdit(): void {
    if (!this.editForm.title_ar?.trim() && !this.editForm.title?.trim() && !this.editForm.title_en?.trim()) {
      this.errorMessage = 'At least one property title is required.';
      return;
    }
    if (!this.editForm.property_type) { this.errorMessage = 'Property type is required.'; return; }
    this.isSubmitting = true;
    this.errorMessage = '';

    this.propertyService.update(this.property!.id, this.editForm).subscribe({
      next: (res) => {
        this.property       = res.data;
        this.isSubmitting   = false;
        this.isEditMode     = false;
        this.successMessage = 'Property updated successfully!';
        this.cdr.detectChanges();
        setTimeout(() => { this.successMessage = ''; this.cdr.detectChanges(); }, 3000);
      },
      error: (err) => {
        this.isSubmitting = false;
        this.errorMessage = err.error?.message || 'Failed to update property.';
        this.cdr.detectChanges();
      },
    });
  }

  finishingLabel(v?: string | null): string { return FINISHINGS.find((f) => f.value === v)?.label ?? '—'; }
  tagLabels(v?: string[] | null): string {
    return v?.length ? v.map((x) => AMENITIES.find((a) => a.value === x)?.label ?? x).join(', ') : '—';
  }
  paymentLabels(v?: string[] | null): string {
    return v?.length ? v.map((x) => PAYMENT_OPTIONS.find((p) => p.value === x)?.label ?? x).join(', ') : '—';
  }

  isTogglingSold = false;

  toggleSold(): void {
    if (!this.property || this.isTogglingSold) return;
    const sold = !this.property.sold_at;
    this.isTogglingSold = true;
    this.errorMessage = '';
    this.propertyService.markSold(this.property.id, sold).subscribe({
      next: (res) => {
        this.property       = { ...this.property!, sold_at: res.data.sold_at ?? null };
        this.isTogglingSold = false;
        this.successMessage = sold
          ? 'Marked as sold. People who saved it were notified and it is hidden from the app lists.'
          : 'Listing reopened.';
        this.cdr.detectChanges();
        setTimeout(() => { this.successMessage = ''; this.cdr.detectChanges(); }, 4000);
      },
      error: (err) => {
        this.isTogglingSold = false;
        this.errorMessage = err.error?.message || 'Failed to update the sold status.';
        this.cdr.detectChanges();
      },
    });
  }

  confirmDelete(): void  { this.showDeleteModal = true; }
  cancelDelete(): void   { this.showDeleteModal = false; }

  deleteProperty(): void {
    this.isDeleting = true;
    this.propertyService.remove(this.property!.id).subscribe({
      next: () => {
        this.isDeleting = false;
        this.router.navigate(['/dashboard/properties']);
      },
      error: () => {
        this.isDeleting      = false;
        this.showDeleteModal = false;
        this.cdr.detectChanges();
      },
    });
  }

  formatPrice(price: number): string {
    if (!price) return '—';
    return new Intl.NumberFormat('ar-EG').format(price) + ' ج.م';
  }

  statusClass(status: string): string {
    const map: Record<string, string> = {
      for_sale:           'status-sale',
      for_rent:           'status-rent',
      for_rent_furnished: 'status-rent-furnished',
    };
    return map[status] || '';
  }

  openLightbox(index: number): void  { this.lightboxIndex = index; }
  closeLightbox(): void               { this.lightboxIndex = null; }
  lightboxPrev(): void {
    if (this.lightboxIndex === null || !this.property) return;
    this.lightboxIndex = (this.lightboxIndex - 1 + this.property.images.length) % this.property.images.length;
  }
  lightboxNext(): void {
    if (this.lightboxIndex === null || !this.property) return;
    this.lightboxIndex = (this.lightboxIndex + 1) % this.property.images.length;
  }
  onLightboxKey(event: KeyboardEvent): void {
    if (event.key === 'Escape')     this.closeLightbox();
    if (event.key === 'ArrowLeft')  this.lightboxPrev();
    if (event.key === 'ArrowRight') this.lightboxNext();
  }

  goBack(): void { this.router.navigate(['/dashboard/properties']); }
}
